import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { SERVICES, INDUSTRIES, IMAGES } from "../data";

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="What We Do"
        title="Our Services."
        subtitle="End-to-end specialised transportation and project logistics solutions — engineered for over-dimensional cargo, wind turbine components and heavy engineering equipment."
        image={IMAGES.serviceWind}
      />

      <section className="py-24 md:py-32 bg-white" data-testid="services-list">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="space-y-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.key} delay={i * 60}>
                <article
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border-2 border-zinc-200 hover:border-brand-green transition-colors p-8 md:p-12"
                  data-testid={`service-item-${s.key}`}
                >
                  <div className="lg:col-span-1">
                    <p className="font-mono text-sm text-brand-orange">0{i + 1}</p>
                  </div>
                  <div className="lg:col-span-5">
                    <h2 className="font-heading text-2xl md:text-4xl leading-tight tracking-tight text-brand-ink">
                      {s.title}
                    </h2>
                  </div>
                  <div className="lg:col-span-6">
                    <p className="font-body text-base md:text-lg text-zinc-700 leading-relaxed">
                      {s.summary}
                    </p>
                    <ul className="mt-6 space-y-2">
                      {s.details.map((d) => (
                        <li key={d} className="flex items-start gap-3 font-body text-sm text-zinc-600">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-brand-green" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32 bg-brand-smoke" data-testid="services-industries">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag">Industries We Serve</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                  Sectors we power with our fleet.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-700 leading-relaxed">
                  While wind energy remains our specialisation, our fleet and expertise support a wider range of
                  infrastructure and heavy-engineering movements across India.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={i * 80}>
                <div
                  className="bg-white border-2 border-zinc-200 hover:border-brand-orange p-6 text-center min-h-[120px] flex items-center justify-center transition-colors"
                  data-testid={`industry-card-${i}`}
                >
                  <p className="font-heading text-lg tracking-tight text-brand-ink">{ind}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-green text-white" data-testid="services-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tightest">
              Have a specialised transportation requirement?
            </h2>
            <p className="font-body text-lg mt-4 text-white/90 max-w-2xl">
              Our operations team will assess your requirement and revert with fleet availability, feasibility inputs and a competitive quote.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="services-cta-contact">
              <Button variant="secondary" size="lg" className="w-full">Talk to Our Team <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
